import { useState } from "react";
import { motion } from "framer-motion";
import { Mandala } from "./Mandala";
import { weddingConfig } from "../config/weddingConfig";

const CURTAIN_EASE = [0.76, 0, 0.24, 1];

export const SealScreen = ({ onOpen, onOpenStart }) => {
  const [opening, setOpening] = useState(false);
  const { couple, seal } = weddingConfig;

  const handleOpen = () => {
    if (opening) return;
    setOpening(true);
    onOpenStart?.();
    window.setTimeout(onOpen, 900);
  };

  return (
    <div
      data-testid="seal-screen"
      className="fixed inset-0 z-50 cursor-pointer"
      role="dialog"
      aria-label="Wedding invitation — tap anywhere to open"
      onClick={handleOpen}
      onWheel={handleOpen}
      onTouchMove={handleOpen}
    >
      {/* curtain halves */}
      <motion.div
        className="absolute inset-y-0 left-0 w-1/2 bg-emerald-deep"
        animate={opening ? { x: "-101%" } : { x: 0 }}
        transition={{ duration: 1, ease: CURTAIN_EASE, delay: 0.35 }}
      />
      <motion.div
        className="absolute inset-y-0 right-0 w-1/2 bg-emerald-deep"
        animate={opening ? { x: "101%" } : { x: 0 }}
        transition={{ duration: 1, ease: CURTAIN_EASE, delay: 0.35 }}
      />
      {/* gold hairline where the halves meet */}
      <motion.div
        className="absolute inset-y-0 left-1/2 w-px bg-gold/30"
        animate={opening ? { opacity: 0 } : { opacity: 1 }}
        transition={{ duration: 0.3 }}
      />

      {/* content */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden px-6 text-center"
        animate={opening ? { opacity: 0, scale: 1.05 } : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.45 }}
      >
        <Mandala className="pointer-events-none absolute left-1/2 top-1/2 h-[150vmin] w-[150vmin] -translate-x-1/2 -translate-y-1/2 animate-[spin_200s_linear_infinite] text-gold/[0.12]" />

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.7 }}
          className="relative font-sans text-[11px] font-medium uppercase tracking-[0.45em] text-gold"
          data-testid="seal-eyebrow"
        >
          {seal.eyebrow}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8 }}
          className="relative mt-7 font-serif text-4xl font-medium text-cream sm:text-5xl"
          data-testid="seal-couple-names"
        >
          {couple.firstName}{" "}
          <span className="font-script text-3xl text-gold-bright sm:text-4xl">
            &
          </span>{" "}
          {couple.secondName}
        </motion.div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.7, duration: 0.8, ease: CURTAIN_EASE }}
          className="relative mt-8 flex w-48 items-center gap-3"
        >
          <span className="h-px flex-1 bg-gold/60" />
          <span className="h-1.5 w-1.5 rotate-45 bg-gold/80" />
          <span className="h-px flex-1 bg-gold/60" />
        </motion.div>

        {/* wax seal */}
        <motion.button
          type="button"
          data-testid="wax-seal-button"
          aria-label="Open the invitation"
          onClick={handleOpen}
          disabled={opening}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={
            opening
              ? { scale: [1, 1.18, 0.85], opacity: [1, 1, 0] }
              : { opacity: 1, scale: 1 }
          }
          transition={
            opening
              ? { duration: 0.85, times: [0, 0.4, 1] }
              : { delay: 0.95, type: "spring", stiffness: 170, damping: 14 }
          }
          className="relative mt-12 flex h-24 w-24 cursor-pointer items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          {!opening && (
            <motion.span
              className="absolute -inset-3 rounded-full border border-gold/50"
              animate={{ scale: [1, 1.3], opacity: [0.55, 0] }}
              transition={{ repeat: Infinity, duration: 2.2, ease: "easeOut" }}
            />
          )}
          <span className="absolute inset-0 rounded-full border border-gold/70" />
          <span className="absolute inset-1.5 rounded-full bg-[radial-gradient(circle_at_35%_30%,#F3E5AB,#D4AF37_48%,#8A6D1F_100%)] shadow-[inset_0_2px_6px_rgba(255,255,255,0.45),inset_0_-4px_8px_rgba(70,50,5,0.55),0_10px_30px_-6px_rgba(212,175,55,0.5)]" />
          <span className="absolute inset-3.5 rounded-full border border-emerald-deep/30" />
          <span className="relative font-serif text-2xl font-semibold tracking-[0.12em] text-emerald-deep">
            {couple.initials}
          </span>
        </motion.button>
      </motion.div>
    </div>
  );
};
